import { createContext, useContext } from "react";
import { type PortalLocale } from "@/lib/i18n/portal";

export type MessageCatalog = Record<string, unknown>;
type TranslationValues = Record<string, string | number | null | undefined>;

export type I18nContextValue = {
  locale: PortalLocale;
  messages: MessageCatalog;
};

export const I18nContext = createContext<I18nContextValue | null>(null);

function getMessage(messages: MessageCatalog, key: string) {
  return key.split(".").reduce<unknown>((current, segment) => {
    if (!current || typeof current !== "object" || !(segment in current)) {
      return undefined;
    }

    return (current as Record<string, unknown>)[segment];
  }, messages);
}

function interpolate(template: string, values?: TranslationValues) {
  if (!values) {
    return template;
  }

  return template.replace(/\{(\w+)\}/g, (match, key) => {
    const value = values[key];
    return value === null || value === undefined ? match : String(value);
  });
}

function useI18nContext() {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error("AdminIntlProvider is required");
  }

  return context;
}

export function useLocale() {
  return useI18nContext().locale;
}

export function useTranslations() {
  const { messages } = useI18nContext();

  return (key: string, values?: TranslationValues) => {
    const message = getMessage(messages, key);
    if (typeof message !== "string") {
      return key;
    }

    return interpolate(message, values);
  };
}
